import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import InvoiceUploader from "@/components/InvoiceUploader";
import TaxBlock from "@/components/TaxBlock";
import { SentBackBanner, ResubmitFooter, hlFor, diffList, fileChange, useFileNames } from "@/components/EditResubmit";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const months = () => Array.from({ length: 24 }, (_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
const filesOf = (e) => [...(e.bill_file_id ? [e.bill_file_id] : []), ...(e.attachment_file_ids || [])];
const linesKey = (ls) => JSON.stringify((ls || []).map(l => [l.description, +l.qty || 0, +l.rate || 0, +l.amount || 0, +l.gst_rate || 0]));

function useMasters() {
  const [m, setM] = useState({ projects: [], vendors: [], gst: [], tds: [], state: "Delhi" });
  useEffect(() => {
    Promise.all([api.get("/v2/expenses/my-projects"), api.get("/v2/payees?role=Vendor,Contractor,Landlord,Other"), api.get("/v2/gst-rates"), api.get("/v2/tds-sections"), api.get("/v2/org-settings")])
      .then(([p, v, g, t, o]) => setM({ projects: p.data, vendors: v.data, gst: g.data, tds: t.data, state: o.data.rkf_state || "Delhi" }));
  }, []);
  return m;
}

export default function ExpenseEditForm({ exp, mode = "edit", onSaved }) {
  const { user } = useAuth();
  const editing = mode === "edit";
  const m = useMasters();
  const [f, setF] = useState({
    project_id: exp.project_id, head_id: exp.head_id, subtype_id: exp.subtype_id, vendor_id: exp.vendor_id, expense_month: exp.expense_month,
    invoice_no: exp.invoice_no || "", no_vendor_bill_no: !!exp.no_vendor_bill_no, invoice_date: (exp.invoice_date || "").slice(0, 10),
    place_of_supply: exp.place_of_supply || "", purpose: exp.purpose || "", notes: exp.notes || "",
    lines: (exp.lines || []).map(l => ({ description: l.description, qty: l.qty || 0, rate: l.rate || 0, amount: l.amount || 0, gst_rate: l.gst_rate || 0 })),
    tds_section_code: exp.tds_section_code || "NONE", tds_rate: exp.tds_rate || 0, tds_override_reason: "", round_off: exp.round_off || 0,
    _ui_totals: { sub: 0, totalGst: 0, invoiceTotal: exp.invoice_total || 0, tdsAmount: 0, netPayable: 0 },
  });
  const [files, setFiles] = useState(filesOf(exp).map(id => ({ id, original_filename: "Current attachment" })));
  useFileNames(filesOf(exp), setFiles);
  const [heads, setHeads] = useState([]);
  const [busy, setBusy] = useState(false);
  const orig = useRef(null);
  const hl = hlFor(editing ? exp.sent_back_fields : []);
  useEffect(() => {
    setHeads([]);
    if (f.project_id) api.get(`/projects/${f.project_id}/expense-lines`).then(r => setHeads(r.data.heads || [])).catch(() => setHeads([]));
  }, [f.project_id]);
  useEffect(() => {
    if (!orig.current && heads.length && f.project_id === exp.project_id) {
      const h = heads.find(x => x.id === exp.head_id);
      orig.current = { head: h?.code || "", sub: exp.subtype_code || "" };
    }
  }, [heads]);
  const subs = (heads.find(h => h.id === f.head_id) || {}).subtypes || [];
  const sub = subs.find(s => s.id === f.subtype_id);
  const vendor = m.vendors.find(v => v.id === f.vendor_id);
  const total = f._ui_totals?.invoiceTotal || 0;
  const room = (sub?.remaining || 0) + (editing && f.subtype_id === exp.subtype_id ? (exp.budget_charge || 0) : 0);
  const pcode = (id) => (m.projects.find(p => p.id === id) || {}).project_code || "";
  const material = f.project_id !== exp.project_id || f.subtype_id !== exp.subtype_id || Math.abs(total - (exp.invoice_total || 0)) > 0.01;
  const changes = diffList([
    ["Project", pcode(exp.project_id), pcode(f.project_id)], ["Head", orig.current?.head, (heads.find(h => h.id === f.head_id) || {}).code],
    ["Sub-type", exp.subtype_code, sub?.code], ["Vendor", exp.vendor_name, vendor?.name || exp.vendor_name],
    ["Amount", Number(exp.invoice_total || 0).toFixed(2), Number(total).toFixed(2)], ["Expense Month", exp.expense_month, f.expense_month],
    ["Vendor bill no.", exp.invoice_no || "", f.no_vendor_bill_no ? "" : f.invoice_no], ["Bill date", (exp.invoice_date || "").slice(0, 10), f.invoice_date],
    ["Purpose", exp.purpose || "", f.purpose], ["Notes", exp.notes || "", f.notes],
  ], [linesKey(exp.lines) !== linesKey(f.lines) ? "Line items edited" : null, fileChange(filesOf(exp), files.map(x => x.id))]);
  const block = !f.subtype_id ? "Pick a sub-type" : !f.vendor_id ? "Pick a vendor" : total > room + 0.01 ? `Invoice total ${inr(total)} exceeds the remaining balance ${inr(room)}` : null;
  const body = () => ({
    project_id: f.project_id, head_id: f.head_id, subtype_id: f.subtype_id, vendor_id: f.vendor_id, expense_month: f.expense_month,
    invoice_no: f.no_vendor_bill_no ? "" : f.invoice_no.trim(), no_vendor_bill_no: !!f.no_vendor_bill_no, invoice_date: f.invoice_date,
    place_of_supply: f.place_of_supply || m.state, purpose: f.purpose, notes: f.notes, round_off: parseFloat(f.round_off || 0),
    lines: f.lines.map(l => ({ description: l.description, qty: parseFloat(l.qty || 0), rate: parseFloat(l.rate || 0), amount: parseFloat(l.amount || 0), gst_rate: parseFloat(l.gst_rate || 0) })),
    tds_section_code: editing ? f.tds_section_code : "NONE", tds_rate: editing ? parseFloat(f.tds_rate || 0) : 0,
  });
  const save = async (action, response) => {
    setBusy(true);
    try {
      if (editing) await api.put(`/v2/expenses/${exp.id}/sent-back-edit`, { ...body(), bill_file_ids: files.map(x => x.id), action, response_to_reviewer: response });
      else await api.post("/v2/expenses", { ...body(), bill_file_id: files[0]?.id || null, submit: true });
      toast.success(editing ? (action === "draft" ? "Draft saved" : "Resubmitted") : "New bill submitted");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>{editing ? `Edit & resubmit · ${exp.inv_ref || exp.invoice_no}` : `New vendor bill · copied from ${exp.inv_ref || exp.invoice_no}`}</DialogTitle></DialogHeader>
      {editing ? <SentBackBanner doc={exp} invRef={exp.inv_ref} /> : <p className="text-xs text-slate-600 rounded bg-slate-50 border p-2" data-testid="exp-copy-note">Pre-filled from rejected bill. Submitting creates a new bill with a new INV number.</p>}
      <div className="space-y-3" data-testid="exp-edit-form">
        <div className="grid grid-cols-3 gap-3">
          <div className={hl("project")}><Label>Project *</Label>
            <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v, head_id: "", subtype_id: "" })}>
              <SelectTrigger data-testid="exp-edit-project"><SelectValue placeholder="Pick project" /></SelectTrigger>
              <SelectContent>{m.projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_code} · {p.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className={hl("budget_line")}><Label>Head *</Label>
            <Select value={f.head_id} onValueChange={(v) => setF({ ...f, head_id: v, subtype_id: "" })} disabled={!heads.length}>
              <SelectTrigger data-testid="exp-edit-head"><SelectValue placeholder="Pick head" /></SelectTrigger>
              <SelectContent>{heads.map(h => <SelectItem key={h.id} value={h.id}>{h.code} · {h.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className={hl("budget_line")}><Label>Sub-Type *</Label>
            <Select value={f.subtype_id} onValueChange={(v) => setF({ ...f, subtype_id: v })} disabled={!f.head_id}>
              <SelectTrigger data-testid="exp-edit-subtype"><SelectValue placeholder="Pick sub-type" /></SelectTrigger>
              <SelectContent>{subs.map(s => <SelectItem key={s.id} value={s.id} data-testid={`exp-edit-sub-${s.code}`}>{s.code} · {s.name} <span className="text-xs text-emerald-600 ml-1">Remaining {inr(s.remaining)}</span></SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className={hl("vendor")}><Label>Vendor *</Label>
            <Select value={f.vendor_id} onValueChange={(v) => setF({ ...f, vendor_id: v })}>
              <SelectTrigger data-testid="exp-edit-vendor"><SelectValue placeholder={exp.vendor_name || "Pick vendor"} /></SelectTrigger>
              <SelectContent>
                {!m.vendors.some(v => v.id === exp.vendor_id) && exp.vendor_id && <SelectItem value={exp.vendor_id}>{exp.vendor_name}</SelectItem>}
                {m.vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className={hl("expense_month")}><Label>Expense Month *</Label>
            <Select value={f.expense_month} onValueChange={(v) => setF({ ...f, expense_month: v })}>
              <SelectTrigger data-testid="exp-edit-month"><SelectValue /></SelectTrigger>
              <SelectContent>{months().map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className={hl("invoice_date")}><Label>Bill date *</Label><Input type="date" value={f.invoice_date} onChange={e => setF({ ...f, invoice_date: e.target.value })} data-testid="exp-edit-date" /></div>
        </div>
        <div className={hl("invoice_number")}><Label>Vendor bill no.</Label>
          <Input disabled={f.no_vendor_bill_no} value={f.no_vendor_bill_no ? "—" : f.invoice_no} onChange={e => setF({ ...f, invoice_no: e.target.value })} data-testid="exp-edit-billno" />
          <label className="flex items-center gap-1.5 text-[11px] mt-1"><input type="checkbox" checked={f.no_vendor_bill_no} onChange={e => setF({ ...f, no_vendor_bill_no: e.target.checked })} data-testid="exp-edit-no-billno" /> No vendor bill number</label>
        </div>
        <div className={`${hl("particulars")} ${hl("amount")}`}>
          <TaxBlock vendor={vendor} rkfState={m.state} gstRates={m.gst} tdsSections={m.tds} value={f} onChange={setF} tdsReadonly={!["Admin", "Accounts"].includes(user?.role)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className={hl("purpose")}><Label>Purpose / Remarks</Label><Textarea rows={2} value={f.purpose} onChange={e => setF({ ...f, purpose: e.target.value })} data-testid="exp-edit-purpose" /></div>
          <div className={hl("notes")}><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="exp-edit-notes" /></div>
        </div>
        <div className={hl("attachments")}><InvoiceUploader label="Bill / Invoice" files={files} onChange={setFiles} testId="exp-edit-files" /></div>
        {editing
          ? <ResubmitFooter kind="expense" doc={exp} changes={changes} material={material} busy={busy} onSave={save} onWithdrawn={onSaved} blockReason={block} />
          : <DialogFooter><Button disabled={busy || !!block} onClick={() => save("create")} data-testid="exp-copy-submit">{busy ? "Saving…" : "Submit new bill"}</Button></DialogFooter>}
      </div>
    </>
  );
}
